import { Link } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ChevronRight, TrendingUp } from 'lucide-react'
import { api } from '@/lib/api/client'
import type { ForecastVersion, ActiveForecastVersion } from '@/types/api'

export default function FinanceSettingsPage() {
  const queryClient = useQueryClient()

  const { data: versions = [], isLoading: versionsLoading } = useQuery({
    queryKey: ['clementine-budget', 'forecast-versions'],
    queryFn:  () => api.get<ForecastVersion[]>('/clementine-budget/forecast-versions'),
  })

  const { data: active } = useQuery({
    queryKey: ['clementine-budget', 'active-version'],
    queryFn:  () => api.get<ActiveForecastVersion>('/clementine-budget/active-version'),
  })

  const setActiveMutation = useMutation({
    mutationFn: (versionId: number) =>
      api.put<{ activeVersionId: number }>('/clementine-budget/active-version', { versionId }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['clementine-budget', 'active-version'] }),
  })

  return (
    <div className="page">
      <Breadcrumb />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <div style={{
          width: 40, height: 40, borderRadius: 10, flexShrink: 0,
          background: 'linear-gradient(135deg, oklch(0.72 0.052 198) 0%, oklch(0.56 0.062 202) 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <TrendingUp size={18} style={{ color: '#fff' }} />
        </div>
        <div>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 600, letterSpacing: '-0.015em' }}>Finance Settings</h1>
          <p className="muted" style={{ margin: 0, fontSize: 13, marginTop: 2 }}>
            Select the forecast version shown on employee Time Reporting dashboards.
          </p>
        </div>
      </div>

      <div className="card" style={{ padding: 24, maxWidth: 640 }}>
        <div style={{ fontWeight: 600, fontSize: 13.5, marginBottom: 4 }}>Active forecast version</div>
        <p className="muted" style={{ fontSize: 12.5, margin: '0 0 18px', lineHeight: 1.5 }}>
          The selected version's revenue budget is shown on each employee's personal budget tracker.
          Only active forecast versions are listed.
        </p>

        {versionsLoading ? (
          <p className="muted" style={{ fontSize: 13 }}>Loading versions…</p>
        ) : versions.length === 0 ? (
          <div style={{
            padding: '14px 16px', borderRadius: 8, fontSize: 13,
            background: 'var(--ns-surface-2)', border: '1px solid var(--ns-border)', color: 'var(--ns-text-muted)',
          }}>
            No forecast versions found. Run a Clementine sync first to populate versions.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {versions.map(v => {
              const isSelected = v.id === active?.activeVersionId
              return (
                <label
                  key={v.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '12px 14px', borderRadius: 8, cursor: 'pointer',
                    border: `1px solid ${isSelected ? 'var(--ns-brand)' : 'var(--ns-border)'}`,
                    background: isSelected
                      ? 'color-mix(in oklch, var(--ns-brand) 8%, var(--ns-surface))'
                      : 'var(--ns-surface)',
                    transition: 'border-color 0.1s, background 0.1s',
                  }}
                >
                  <input
                    type="radio"
                    name="forecast-version"
                    value={v.id}
                    checked={isSelected}
                    onChange={() => setActiveMutation.mutate(v.id)}
                    style={{ accentColor: 'var(--ns-brand)', width: 15, height: 15, flexShrink: 0 }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: isSelected ? 600 : 400 }}>{v.name}</div>
                    <div className="muted" style={{ fontSize: 11.5, marginTop: 2, display: 'flex', gap: 8 }}>
                      {v.isBudget && <span>Budget</span>}
                      {v.isCurrentDefault && <span>Default</span>}
                      {v.forecastStart && v.forecastEnd && (
                        <span>{v.forecastStart.slice(0, 7)} – {v.forecastEnd.slice(0, 7)}</span>
                      )}
                    </div>
                  </div>
                  {isSelected && (
                    <span className="badge badge-ok" style={{ fontSize: 11, flexShrink: 0 }}>
                      <span className="dot dot-ok" />
                      Active
                    </span>
                  )}
                </label>
              )
            })}
          </div>
        )}

        {setActiveMutation.isError && (
          <p style={{ marginTop: 12, fontSize: 12.5, color: 'var(--ns-err)' }}>
            Failed to save: {(setActiveMutation.error as Error).message}
          </p>
        )}
        {setActiveMutation.isSuccess && !setActiveMutation.isPending && (
          <p className="muted" style={{ marginTop: 12, fontSize: 12.5 }}>Saved.</p>
        )}
      </div>
    </div>
  )
}

function Breadcrumb() {
  return (
    <div className="row" style={{ gap: 4, marginBottom: 14, color: 'var(--ns-text-muted)', fontSize: 12 }}>
      <Link to="/settings/integrations" style={{ color: 'inherit' }}>Settings</Link>
      <ChevronRight size={11} />
      <span style={{ color: 'var(--ns-text)' }}>Finance</span>
    </div>
  )
}
